import { useEffect, useMemo, useState } from 'react'
import { Typeahead } from '@/components/forms/Typeahead'
import { createLatestSearch } from './latestSearch'

export const PRODUCT_PICKER_LIMIT = 30
const SEARCH_DEBOUNCE_MS = 250

export interface PickerProduct {
  id: string
  code: string
  name: string
}

export function productPickerLabel(p: Pick<PickerProduct, 'code' | 'name'>): string {
  return p.name ? `${p.code} — ${p.name}` : p.code
}

interface ProductSearchPickerProps<T extends PickerProduct> {
  /** Tìm phía server theo từ khóa; trả tối đa `limit` dòng. Chuỗi rỗng = trả danh sách đầu. Truyền hàm ổn định (khai báo ngoài component). */
  search: (query: string, limit: number) => Promise<T[]>
  value: T | null
  onChange: (product: T | null) => void
  className?: string
}

/**
 * Ô chọn mã hàng vừa gõ vừa chọn, nhẹ cho danh mục lớn:
 * - không nạp cả danh mục vào trang; mỗi lần chỉ giữ tối đa PRODUCT_PICKER_LIMIT mã;
 * - gõ xong chờ 250ms mới gọi server (không gọi mỗi phím);
 * - kết quả trả về trễ của lần gõ cũ bị bỏ, không ghi đè kết quả mới.
 */
export function ProductSearchPicker<T extends PickerProduct>({
  search,
  value,
  onChange,
  className = '',
}: ProductSearchPickerProps<T>) {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<T[]>([])
  const [searching, setSearching] = useState(false)
  const [failed, setFailed] = useState(false)
  const controller = useMemo(
    () =>
      createLatestSearch<T>({
        search: (q) => search(q, PRODUCT_PICKER_LIMIT),
        debounceMs: SEARCH_DEBOUNCE_MS,
        onResult: (items) => {
          setFailed(false)
          setOptions(items.slice(0, PRODUCT_PICKER_LIMIT))
        },
        onError: (err) => {
          console.error('Product search failed:', err)
          setOptions([])
          setFailed(true)
        },
        onBusy: setSearching,
      }),
    [search],
  )

  useEffect(() => {
    const q = query.trim()
    // Ô vừa được điền chữ của mã đã chọn (không phải người dùng gõ) thì không tìm lại.
    if (value && q === productPickerLabel(value)) return
    controller.run(q)
  }, [query, value, controller])

  useEffect(() => () => controller.cancel(), [controller])

  const handleInputChange = (text: string) => {
    setQuery(text)
    // Người dùng sửa chữ sau khi đã chọn: bỏ lựa chọn cũ để nút "Thêm" không thêm nhầm mã cũ.
    if (value && text !== productPickerLabel(value)) onChange(null)
  }

  return (
    <div className={className}>
      <Typeahead<T>
        items={options}
        value={value}
        onChange={onChange}
        onInputChange={handleInputChange}
        displayValue={productPickerLabel}
        getItemKey={(p) => p.id}
        // Server đã lọc theo từ khóa; không lọc lại ở client.
        filterFn={() => true}
        renderItem={(p) => (
          <div className="min-w-0">
            <div className="truncate text-xs font-semibold">{p.code}</div>
            {p.name && <div className="truncate text-[11px] text-slate-500">{p.name}</div>}
          </div>
        )}
        placeholder={searching ? 'Đang tìm…' : 'Gõ mã hoặc tên mã hàng…'}
        aria-label="Chọn mã hàng để thêm"
        inputClassName="text-xs"
      />
      {failed && <p className="mt-1 text-[11px] text-red-600">Không tìm được mã hàng. Kiểm tra kết nối rồi gõ lại.</p>}
      {!failed && query.trim() && !searching && options.length === 0 && !value && (
        <p className="mt-1 text-[11px] text-slate-500">Không tìm thấy mã hàng phù hợp.</p>
      )}
      {options.length >= PRODUCT_PICKER_LIMIT && (
        <p className="mt-1 text-[11px] text-slate-500">Hiện {PRODUCT_PICKER_LIMIT} mã đầu — gõ thêm để thu hẹp.</p>
      )}
    </div>
  )
}
